import React from 'react';
import { Table, Button } from 'react-bootstrap';
import moment from 'moment';
import 'moment/locale/it';
import { downloadFile } from '../utilities/NetUtilities';

moment.locale('it');

const TabellaSintesiSettimana = ({ turni, sedi, servizi, lavoratori, excelUrl }) => {
    if (!turni || !Object.keys(turni).length) {
        return <p>Nessun dato da visualizzare.</p>;
    }

    const days = Object.keys(turni);
    const fasce = ['mattina', 'pomeriggio'];
    const statoPostazioni = ['Occupate', 'Aperte'];
    const backofficeStyle = { backgroundColor: '#d1d9e6' };

    const getWorkerName = (id) => {
        const worker = lavoratori.find((l) => l.id === id);
        return worker ? `${worker.nome} ${worker.cognome}` : id;
    };

    const getServiceName = (id) => servizi[id]?.[0] || '';
    const isBackoffice = (id) => getServiceName(id).toLowerCase() === 'backoffice';

    const columns = days.flatMap((day, dayIndex) =>
        fasce.map((shiftType, shiftIndex) => ({ day, shiftType, dayIndex, shiftIndex })));


    const buildTableData = () => {
        const tableData = [];
        sedi.forEach((sede, sedeIndex) => {
            const postazioni = sede.postazioni || {};
            Object.entries(postazioni).forEach(([postId, postName], postIdx) => {
                const cellMatrix = {};
                columns.forEach(({ day, shiftType }) => {
                    const key = `${day}-${shiftType}`;
                    const shifts = (turni[day]?.[sede.id]?.[postId]?.[shiftType] || []).map(shift => ({
                        ...shift,
                        formatted: `${shift.inizio}-${shift.fine} ${getWorkerName(shift.lavoratoreId)} (${getServiceName(shift.servizioId)})`,
                        isBackoffice: isBackoffice(shift.servizioId)
                    }));
                    cellMatrix[key] = shifts;
                });
                const maxRows = Math.max(...Object.values(cellMatrix).map(arr => arr.length), 1);
                const rows = [];
                for (let i = 0; i < maxRows; i++) {
                    const row = {};
                    columns.forEach(({ day, shiftType }) => {
                        const key = `${day}-${shiftType}`;
                        row[key] = cellMatrix[key][i] || null;
                    });
                    rows.push(row);
                }
                tableData.push({
                    sede,
                    postId,
                    postName,
                    rows,
                    rowSpan: maxRows,
                    sedeIndex,
                    postIdx
                });
            });
        });
        return tableData;
    };

    const tableData = buildTableData();

    const calculateDailyStats = () => {
        const stats = {};
        days.forEach((day) => {
            stats[day] = {
                mattina: { occupate: 0, aperte: 0 },
                pomeriggio: { occupate: 0, aperte: 0 }
            };
            sedi.forEach(sede => {
                Object.keys(sede.postazioni || {}).forEach(postId => {
                    fasce.forEach(shiftType => {
                        const shifts = turni[day]?.[sede.id]?.[postId]?.[shiftType] || [];
                        const isOccupied = shifts.length > 0;
                        const isOpen = isOccupied ? !shifts.some(s => isBackoffice(s.servizioId)) : true;
                        stats[day][shiftType].occupate += isOccupied ? 1 : 0;
                        stats[day][shiftType].aperte += isOpen ? 1 : 0;
                    });
                });
            });
        });
        return stats;
    };

    const dailyStats = calculateDailyStats();

    const renderTableHeaders = () => (
        <thead className='intestazioneBloccata'>
            <tr>
                <th className ="align-middle inEvidenza bordiGrigi " id="header-sede" rowSpan="2">Sede</th>
                <th className ="align-middle inEvidenza bordiGrigi " id="header-postazione" rowSpan="2">Postazione</th>
                {days.map((day, dayIndex) => (
                    <th
                        id={`header-day-${dayIndex}`}
                        key={dayIndex}
                        colSpan={fasce.length}
                        className="text-center inEvidenza bordiGrigi "
                    >
                        {moment(day).format('dddd DD MMMM')}
                    </th>
                ))}
            </tr>
            <tr>
                {days.map((day, dayIndex) =>
                    fasce.map((shiftType, shiftIndex) => (
                        <th
                            id={`header-day-${dayIndex}-shift-${shiftIndex}`}
                            key={`${dayIndex}-${shiftIndex}`}
                            className="text-center inEvidenza bordiGrigi "
                        >
                            {shiftType}
                        </th>
                    ))
                )}
            </tr>
        </thead>
    );

    const renderShiftRows = () => {
        let lastSedeId = null;
        let sedeRowSpan = 0;
        const rows = [];
    
        tableData.forEach((item, tableIdx) => {
            const isNewSede = item.sede.id !== lastSedeId;
            
            if (isNewSede) {
                lastSedeId = item.sede.id;
                sedeRowSpan = tableData
                    .filter(i => i.sede.id === item.sede.id)
                    .reduce((sum, post) => sum + post.rows.length, 0);
            }
    
            item.rows.forEach((row, rowIndex) => {
                rows.push(
                    <tr key={`row-${item.sede.id}-${item.postId}-${rowIndex}`}>
                        {isNewSede && rowIndex === 0 && (
                            <td 
                                id={`sede-${item.sedeIndex}-post-${item.postIdx}-sede`}
                                rowSpan={sedeRowSpan}
                                className="align-middle inEvidenza bordiGrigi "
                            >
                                {item.sede.descrizione}
                            </td>
                        )}
                        
                        {rowIndex === 0 && (
                            <td 
                                id={`sede-${item.sedeIndex}-post-${item.postIdx}-post`}
                                rowSpan={item.rows.length}
                                className="align-middle inEvidenza bordiGrigi "
                            >
                                {item.postName}
                            </td>
                        )}
                        
                        {columns.map((col, colIndex) => {
                            const key = `${col.day}-${col.shiftType}`;
                            const cellData = row[key];
                            const cellStyle = cellData?.isBackoffice ? backofficeStyle : null;
                            
                            return (
                                <td
                                    id={`cell-${tableIdx}-${rowIndex}-${colIndex}`}
                                    key={`cell-${tableIdx}-${rowIndex}-${colIndex}`}
                                    className="text-center bordiGrigi "
                                    style={cellStyle}
                                >
                                    {cellData ? cellData.formatted : '-'}
                                </td>
                            );
                        })}
                    </tr>
                );
            });
        });
    
        return rows;
    };

    const renderTotalRows = () => (
        statoPostazioni.map((type, typeIdx) => (
            <tr key={`total-${type}-${typeIdx}`}>
                <td id={`total-label-${typeIdx}`} className="inEvidenza bordiGrigi " colSpan="2">Totale Postazioni {type}</td>
                {days.map((day, dayIndex) =>
                    fasce.map((shiftType, shiftIndex) => (
                        <td
                            id={`total-${type}-${dayIndex}-${shiftIndex}`}
                            key={`total-${type}-${dayIndex}-${shiftIndex}`}
                            className="text-center inEvidenza bordiGrigi "
                        >
                            {dailyStats[day][shiftType][type.toLowerCase()]}
                        </td>
                    ))
                )}
            </tr>
        ))
    );

    return (
        <div>
            <div className="mb-1 d-flex justify-content-end">
                <Button
                    data-toggle="tooltip"
                    title="Scarica Excel"
                    className="btnGeneraleVariante"
                    variant="flat"
                    size="sm"
                    onClick={() => downloadFile(excelUrl, "sintesi-settimana.xlsx")}
                >
                    scarica
                </Button>
            </div>
            <Table bordered className='tableIntestazioneBloccata'>
                {renderTableHeaders()}
                <tbody>
                    {renderShiftRows()}
                    {renderTotalRows()}
                </tbody>
            </Table>
        </div>
    );
};

export default TabellaSintesiSettimana;